// Component architecture graph visualization - 198 lines ✅
import { useCallback } from 'react'
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow'
import 'reactflow/dist/style.css'

const componentNodes: Node[] = [
  // Frontend Layer
  { id: 'dashboard', position: { x: 50, y: 50 }, data: { label: 'Dashboard' }, style: { background: '#4A90E2', color: 'white', padding: 8, borderRadius: 8, fontSize: 12 } },
  { id: 'issues-page', position: { x: 200, y: 50 }, data: { label: 'Issues' }, style: { background: '#4A90E2', color: 'white', padding: 8, borderRadius: 8, fontSize: 12 } },
  { id: 'kb-page', position: { x: 350, y: 50 }, data: { label: 'Knowledge Base' }, style: { background: '#4A90E2', color: 'white', padding: 8, borderRadius: 8, fontSize: 12 } },
  { id: 'documents-page', position: { x: 500, y: 50 }, data: { label: 'Documents' }, style: { background: '#4A90E2', color: 'white', padding: 8, borderRadius: 8, fontSize: 12 } },
  { id: 'architecture-page', position: { x: 650, y: 50 }, data: { label: 'Architecture' }, style: { background: '#4A90E2', color: 'white', padding: 8, borderRadius: 8, fontSize: 12 } },

  // Database Layer
  { id: 'postgresql', position: { x: 300, y: 200 }, data: { label: 'PostgreSQL' }, style: { background: '#336791', color: 'white', padding: 10, borderRadius: 8, fontWeight: 'bold' } },
  { id: 'pgvector', position: { x: 450, y: 200 }, data: { label: 'pgvector' }, style: { background: '#336791', color: 'white', padding: 8, borderRadius: 8 } },
  { id: 'auth', position: { x: 600, y: 200 }, data: { label: 'Supabase Auth' }, style: { background: '#3ECF8E', color: 'white', padding: 8, borderRadius: 8 } },

  // Edge Functions Layer
  { id: 'issue-recorder', position: { x: 50, y: 350 }, data: { label: 'issue-recorder' }, style: { background: '#9B59B6', color: 'white', padding: 6, borderRadius: 6, fontSize: 11 } },
  { id: 'kb-search', position: { x: 180, y: 350 }, data: { label: 'kb-search' }, style: { background: '#9B59B6', color: 'white', padding: 6, borderRadius: 6, fontSize: 11 } },
  { id: 'kb-manager', position: { x: 310, y: 350 }, data: { label: 'kb-manager' }, style: { background: '#9B59B6', color: 'white', padding: 6, borderRadius: 6, fontSize: 11 } },
  { id: 'upload-doc', position: { x: 440, y: 350 }, data: { label: 'upload-document' }, style: { background: '#9B59B6', color: 'white', padding: 6, borderRadius: 6, fontSize: 11 } },
  { id: 'process-doc', position: { x: 570, y: 350 }, data: { label: 'process-document' }, style: { background: '#9B59B6', color: 'white', padding: 6, borderRadius: 6, fontSize: 11 } },
  { id: 'send-email', position: { x: 700, y: 350 }, data: { label: 'send-email' }, style: { background: '#9B59B6', color: 'white', padding: 6, borderRadius: 6, fontSize: 11 } },

  // AI Layer
  { id: 'kaycoo', position: { x: 250, y: 500 }, data: { label: 'Kaycoo Platform' }, style: { background: '#E67E22', color: 'white', padding: 10, borderRadius: 8, fontWeight: 'bold' } },
  { id: 'openai', position: { x: 450, y: 500 }, data: { label: 'OpenAI' }, style: { background: '#10A37F', color: 'white', padding: 10, borderRadius: 8, fontWeight: 'bold' } },
]

const componentEdges: Edge[] = [
  // Frontend to Database
  { id: 'issues-db', source: 'issues-page', target: 'postgresql', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'kb-db', source: 'kb-page', target: 'postgresql', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'kb-vector', source: 'kb-page', target: 'pgvector', animated: true, style: { stroke: '#4ECDC4' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'docs-db', source: 'documents-page', target: 'postgresql', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'auth-conn', source: 'dashboard', target: 'auth', label: 'auth', markerEnd: { type: MarkerType.ArrowClosed } },

  // Database to Edge Functions
  { id: 'db-recorder', source: 'postgresql', target: 'issue-recorder', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'db-kb-search', source: 'postgresql', target: 'kb-search', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'db-kb-manager', source: 'postgresql', target: 'kb-manager', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'vector-search', source: 'pgvector', target: 'kb-search', style: { stroke: '#4ECDC4' }, markerEnd: { type: MarkerType.ArrowClosed } },

  // Edge Functions to AI
  { id: 'recorder-ai', source: 'issue-recorder', target: 'kaycoo', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'kb-search-ai', source: 'kb-search', target: 'kaycoo', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'kb-manager-ai', source: 'kb-manager', target: 'kaycoo', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'openai-embed', source: 'process-doc', target: 'openai', label: 'embeddings', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'kaycoo-openai', source: 'kaycoo', target: 'openai', style: { strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed } },

  // Document pipeline
  { id: 'upload-process', source: 'upload-doc', target: 'process-doc', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'process-db', source: 'process-doc', target: 'postgresql', markerEnd: { type: MarkerType.ArrowClosed } },
]

export function ComponentGraph() {
  const [nodes, , onNodesChange] = useNodesState(componentNodes)
  const [edges, , onEdgesChange] = useEdgesState(componentEdges)

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    console.log('Clicked component:', node.data.label)
  }, [])

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-4">
        <h3 className="mb-2 text-lg font-semibold text-kaycoo-teal-dark">Component Architecture Graph</h3>
        <p className="text-sm text-kaycoo-charcoal-2">
          Visual map of frontend pages, database, Edge Functions, and AI services with their connections.
        </p>
      </div>

      <div className="h-[600px] w-full rounded-lg border border-kaycoo-teal-dark bg-white">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          fitView
          attributionPosition="bottom-left"
        >
          <Background color="#e5e7eb" gap={16} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              const bg = node.style?.background as string
              return bg || '#9CD6D6'
            }}
            maskColor="rgba(0, 60, 65, 0.1)"
          />
        </ReactFlow>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <LegendItem color="#4A90E2" label="Frontend Pages" />
        <LegendItem color="#336791" label="Database" />
        <LegendItem color="#9B59B6" label="Edge Functions" />
        <LegendItem color="#E67E22" label="Kaycoo Platform" />
        <LegendItem color="#10A37F" label="OpenAI" />
      </div>
    </div>
  )
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded border border-kaycoo-teal bg-white p-2">
      <div className="h-4 w-4 rounded" style={{ background: color }} />
      <span className="text-sm text-kaycoo-charcoal-2">{label}</span>
    </div>
  )
}
